import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router";
import { AuthProvider } from "./auth/AuthProvider";
import { AnalyticsIdentity } from "./components/AnalyticsIdentity";
import { ConsentBanner } from "./components/ConsentBanner";
import { detectLocale } from "./i18n";
import { ApiError } from "./lib/api";
import { startAnalytics, trackPageView } from "./lib/gtm";
import { router } from "./router";
import "./index.css";

/*
 * Before React, deliberately.
 *
 * This writes the Consent Mode defaults and only then injects the container, so
 * the ordering that makes consent enforceable is settled before any component
 * can mount and push an event. Doing it in an effect would put the first
 * pageview of the session in a race with the defaults that govern it.
 *
 * It is a no-op when the build carries no container id.
 */
startAnalytics();

/*
 * Pageviews, straight off the router rather than through a component.
 *
 * A navigation is a router event, and `subscribe` is where the router publishes
 * them. The alternative — a `useLocation` effect — needs a component inside the
 * route tree, and since the signed-out pages are siblings of the authenticated
 * tree rather than children of it, that would have meant wrapping every route
 * in a pathless layout to gain nothing.
 *
 * `subscribe` fires on navigations only, so the first view is pushed here.
 */
trackPageView(window.location.pathname + window.location.search);
router.subscribe((state) => {
  trackPageView(state.location.pathname + state.location.search);
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      // A 401 that survived the refresh retry, or any 4xx, will not fix itself.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.status < 500 ? false : failureCount < 2,
    },
  },
});

const container = document.getElementById("root");
if (!container) throw new Error("Missing #root element");

void detectLocale().finally(() =>
  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {/* Both are siblings of the router rather than routes: the banner has
              to appear on the signed-out pages too, and identity is a property of
              the session rather than of the page being viewed. */}
          <AnalyticsIdentity />
          <RouterProvider router={router} />
          <ConsentBanner />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  ),
);
